/**
 * Pages/Panel/Recibos/Recibos.js
 *
 * Consulta masiva de recibos de nómina (timbrados) filtrando por rango de
 * fechas de pago, empleados y/o departamentos, con descarga masiva en ZIP
 * (XML, PDF, o ambos) de los recibos seleccionados en la tabla.
 */

import React, { Component } from "react";
import { message, PageHeader, Spin, Row, Col, Space, Tag, Radio } from "antd";
import axios from "axios";
import { connect } from "react-redux";
import moment from "moment";
import "moment/locale/es";
import {
  Form, FormItem, TableItem, ButtonItem, ErrorItem,
  RangePickerItem, dineroDisabledMask,
} from "../../../Components/Items";
import SelectEmpleadosItem from "../../../Components/SelectEmpleadosItem";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import ModalFormatoDescarga from "../../../Components/ModalFormatoDescarga";
import ModalEnviarRecibos from "../../../Components/ModalEnviarRecibos";
import descargarRecibosMasivo from "../../../Components/DescargarRecibosMasivo";
import {
  OPCIONES_FINIQUITO, filtrarPorFiniquito, hayFiniquitos, sufijoFiniquito,
} from "../PreNomina/filtroFiniquito";
import { FileTextOutlined, DownloadOutlined, MailOutlined } from "@ant-design/icons";

moment.locale("es");

// Los campos de texto llegan vacíos cuando el empleado no tiene departamento o
// el CFDI todavía no trae uuid, y localeCompare truena con undefined.
const txt = (v) => (v === null || v === undefined ? "" : v.toString());
const ms = (v) => (v ? new Date(v).getTime() : 0);

/**
 * Orden de los periodos. NO va por `prenominaNombre`: es texto libre que el
 * usuario escribe, y alfabéticamente "Semana 10" queda antes que "Semana 2".
 * Se ordena por la fecha de inicio del periodo, con la fecha de pago como
 * segundo criterio para los que abren el mismo día (la nómina ordinaria y un
 * finiquito, por ejemplo), y el nombre sólo como último recurso cuando la
 * prenómina no tiene fechas.
 */
const compararPeriodo = (a, b) =>
  ms(a.fechaInicio) - ms(b.fechaInicio) ||
  ms(a.fechaPago) - ms(b.fechaPago) ||
  txt(a.prenominaNombre).localeCompare(txt(b.prenominaNombre));

class Recibos extends Component {
  state = {
    loading: false,
    consultado: false,
    recibos: [],
    seleccionados: [],
    // 'todos' | 'sin' | 'solo' (ver PreNomina/filtroFiniquito.js). Filtra en el navegador lo
    // que ya trajo la consulta. Abre en 'todos': aquí se viene a BUSCAR un recibo, y esconder
    // los finiquitos de entrada haría creer que no existen.
    filtroFiniquito: "todos",
    descargaModal: { visible: false, loading: false },
    envioModal: { visible: false },
  };

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  async consultar(values) {
    this.setState({ loading: true, seleccionados: [], filtroFiniquito: "todos" });
    const [fechaInicio, fechaFin] = values.periodo || [];
    try {
      const res = await axios.post(
        "prenomina/consultarRecibosMasivo",
        {
          empresaId: this.props.empresaId,
          // Se manda el DÍA, no el instante: fechaPago es una fecha-día anclada al
          // mediodía UTC y un ISO con hora local corría el rango un día.
          fechaInicio: fechaInicio ? fechaInicio.format("YYYY-MM-DD") : null,
          fechaFin: fechaFin ? fechaFin.format("YYYY-MM-DD") : null,
          empleadoIds: values.empleadoIds || [],
          departamentoIds: values.departamentoIds || [],
        },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        // Se guardan ya ordenados por empleado. El servidor los devuelve en el
        // orden de las líneas de cada prenómina, que aquí no significa nada, y
        // como el sort de la tabla es estable este orden es el que queda dentro
        // de cada empate — con el orden por periodo de abajo, eso da "periodo,
        // luego empleado" tanto ascendente como descendente.
        const recibos = [...(res.data.recibos || [])].sort((a, b) =>
          txt(a.nombreEmpleado).localeCompare(txt(b.nombreEmpleado))
        );
        this.setState({ recibos, consultado: true });
      } else {
        message.error(res.data.message || "Error al consultar los recibos");
      }
    } catch {
      message.error("Error al consultar los recibos");
    }
    this.setState({ loading: false });
  }

  abrirDescarga() {
    if (this.state.seleccionados.length === 0) {
      message.info("Selecciona al menos un recibo");
      return;
    }
    this.setState({ descargaModal: { visible: true, loading: false } });
  }

  abrirEnvio() {
    if (this.state.seleccionados.length === 0) {
      message.info("Selecciona al menos un recibo");
      return;
    }
    this.setState({ envioModal: { visible: true } });
  }

  async confirmarDescarga(formato) {
    this.setState(prev => ({ descargaModal: { ...prev.descargaModal, loading: true } }));
    await descargarRecibosMasivo({
      empresaId: this.props.empresaId,
      headersToken: this.props.headersToken,
      lineaIds: this.state.seleccionados,
      formato,
      nombreZip: `recibos_nomina_${moment().format("YYYY-MM-DD")}${sufijoFiniquito(this.state.filtroFiniquito)}`,
    });
    this.setState({ descargaModal: { visible: false, loading: false } });
  }

  render() {
    const { loading, consultado, recibos, seleccionados, filtroFiniquito, descargaModal, envioModal } = this.state;
    const conFiniquitos = hayFiniquitos(recibos);
    const visibles = filtrarPorFiniquito(recibos, filtroFiniquito);

    const columns = [
      {
        title: "Empleado", dataIndex: "nombreEmpleado", key: "nombreEmpleado",
        buscar: "nombreEmpleado",
        sorter: (a, b) => txt(a.nombreEmpleado).localeCompare(txt(b.nombreEmpleado)),
      },
      {
        title: "Departamento", dataIndex: "departamento", key: "departamento",
        buscar: "departamento",
        sorter: (a, b) => txt(a.departamento).localeCompare(txt(b.departamento)),
      },
      {
        title: "Periodo", dataIndex: "prenominaNombre", key: "prenominaNombre",
        buscar: "prenominaNombre",
        sorter: compararPeriodo,
        // El orden con el que se abre la tabla: el periodo más reciente primero,
        // que es el que se viene a consultar después de cerrar una nómina.
        defaultSortOrder: "descend",
      },
      {
        // El finiquito cae dentro de la prenómina ordinaria, así que el periodo solo no lo
        // distingue. Morado = el color de los finiquitos en Prenómina.
        title: "Tipo", dataIndex: "esFiniquito", key: "esFiniquito",
        render: (v) => (v ? <Tag color="purple">Finiquito</Tag> : <Tag>Nómina</Tag>),
        sorter: (a, b) => Number(!!a.esFiniquito) - Number(!!b.esFiniquito),
      },
      {
        title: "Fecha de pago", dataIndex: "fechaPago", key: "fechaPago",
        render: (v) => (v ? moment(v).format("DD/MM/YYYY") : ""),
        sorter: (a, b) => ms(a.fechaPago) - ms(b.fechaPago),
      },
      {
        title: "UUID", dataIndex: "uuid", key: "uuid",
        // Se busca también en `uuids` porque un finiquito con pago por separación
        // trae dos CFDI y la columna sólo enseña el ordinario: pegar el UUID del
        // de separación no encontraría su renglón.
        buscar: ["uuid", "uuids"],
        render: (v) => v ? <Tag>{v}</Tag> : "",
      },
      {
        title: "Total Neto", dataIndex: "totalNeto", key: "totalNeto", align: "right",
        render: (v) => dineroDisabledMask(v || 0),
        sorter: (a, b) => (a.totalNeto || 0) - (b.totalNeto || 0),
      },
    ];

    return (
      <Form
        name="recibosMasivos"
        onFinish={this.consultar.bind(this)}
        onFinishFailed={this.onFinishFailed}
        initialValues={{
          periodo: [moment().startOf("year"), moment().endOf("month")],
          empleadoIds: [],
          departamentoIds: [],
        }}
      >
        <PageHeader
          onBack={this.atras.bind(this)}
          title={(
            <span>
              Recibos de Nómina &nbsp;<FileTextOutlined />
            </span>
          )}
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
          extra={
            <Space>
              {consultado && (
                <ButtonItem
                  blanco
                  icon={<DownloadOutlined />}
                  disabled={seleccionados.length === 0}
                  onClick={() => this.abrirDescarga()}
                >
                  Descargar seleccionados ({seleccionados.length})
                </ButtonItem>
              )}
              {consultado && (
                <ButtonItem
                  blanco
                  icon={<MailOutlined />}
                  disabled={seleccionados.length === 0}
                  onClick={() => this.abrirEnvio()}
                >
                  Enviar seleccionados ({seleccionados.length})
                </ButtonItem>
              )}
              <ButtonItem type="primary" htmlType="submit">
                Consultar
              </ButtonItem>
            </Space>
          }
        />

        <Spin spinning={loading}>
          <div
            style={{
              backgroundColor: "white",
              padding: 20,
              paddingTop: 10,
              borderRadius: 5,
              marginBottom: 10,
              boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
            }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={8}>
                <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>
                  Rango de fechas de pago
                </label>
                <FormItem name="periodo" required>
                  <RangePickerItem style={{ width: "100%" }} />
                </FormItem>
              </Col>
              <Col xs={24} md={8}>
                <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>
                  Empleados (opcional)
                </label>
                <FormItem name="empleadoIds" noStyle>
                  <SelectEmpleadosItem
                    mode="multiple"
                    incluirBajas
                    empresaId={this.props.empresaId}
                    headersToken={this.props.headersToken}
                    placeholder="Todos los empleados"
                    style={{ width: "100%" }}
                  />
                </FormItem>
              </Col>
              <Col xs={24} md={8}>
                <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>
                  Departamentos (opcional)
                </label>
                <FormItem name="departamentoIds" noStyle>
                  <SelectDepartamentosItem
                    mode="multiple"
                    empresaId={this.props.empresaId}
                    headersToken={this.props.headersToken}
                    placeholder="Todos los departamentos"
                    style={{ width: "100%" }}
                  />
                </FormItem>
              </Col>
            </Row>
          </div>

          {consultado && (
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <div style={{
                display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                marginBottom: recibos.length === 0 ? 4 : 8,
              }}>
                <h3 style={{ margin: 0 }}>
                  {visibles.length === recibos.length
                    ? `${recibos.length} recibo(s) encontrado(s)`
                    : `${visibles.length} de ${recibos.length} recibo(s)`}
                </h3>
                {/* Sólo si la consulta trajo algún finiquito; sin ellos no hay nada que separar.
                    Cambiar la variante limpia la selección, para no descargar ni enviar
                    renglones que ya no se ven. */}
                {conFiniquitos && (
                  <Radio.Group
                    size="small"
                    optionType="button"
                    buttonStyle="solid"
                    value={filtroFiniquito}
                    onChange={(e) => this.setState({ filtroFiniquito: e.target.value, seleccionados: [] })}
                    options={OPCIONES_FINIQUITO.map((o) => ({ label: o.etiqueta, value: o.valor }))}
                  />
                )}
              </div>
              {recibos.length === 0 && (
                <p style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
                  Solo se listan recibos ya timbrados, y el rango se compara contra la
                  fecha de pago del periodo (no contra las fechas de inicio y fin).
                </p>
              )}
              <TableItem
                rowKey={(v) => v.lineaId}
                columns={columns}
                dataSource={visibles}
                size="small"
                rowSelection={{
                  selectedRowKeys: seleccionados,
                  onChange: (keys) => this.setState({ seleccionados: keys }),
                }}
              />
            </div>
          )}
        </Spin>

        <ModalFormatoDescarga
          visible={descargaModal.visible}
          loading={descargaModal.loading}
          onClose={() => this.setState({ descargaModal: { visible: false, loading: false } })}
          onConfirm={(formato) => this.confirmarDescarga(formato)}
        />

        <ModalEnviarRecibos
          visible={envioModal.visible}
          lineaIds={seleccionados}
          onClose={() => this.setState({ envioModal: { visible: false } })}
        />
      </Form>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(Recibos);
